import { formatIngestionRunEnumLabel, isExtractionRun } from './beerIngestionRunFilters';

/** Canonical extraction pipeline stages, matching backend INGESTION_RUN_STAGES. */
export const INGESTION_RUN_STAGES = [
  'queued',
  'scraping',
  'normalizing',
  'comparing',
  'applying',
  'applied',
];

const INGESTION_RUN_STAGE_ALIASES = {
  completed: 'applied',
  complete: 'applied',
  reviewing: 'comparing',
  review: 'comparing',
};

const IN_PROGRESS_STAGES = new Set(['queued', 'scraping', 'applying']);

export const INGESTION_STAGE_STATE_COMPLETED = 'completed';
export const INGESTION_STAGE_STATE_CURRENT = 'current';
export const INGESTION_STAGE_STATE_UPCOMING = 'upcoming';
export const INGESTION_STAGE_STATE_ERROR = 'error';

function readNormalizedStage(value) {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim().toLowerCase();
}

function inferReachedStage(run) {
  if (run?.applyStatus === 'applied') {
    return 'applied';
  }
  if (run?.applyStatus === 'applying' || run?.applyStatus === 'partial') {
    return 'applying';
  }
  if (run?.hasComparison) {
    return 'comparing';
  }
  if (run?.hasValidatedSnapshot) {
    return 'normalizing';
  }
  if (run?.hasSnapshot) {
    return 'scraping';
  }
  if (run?.status === 'pending') {
    return 'queued';
  }
  return null;
}

export function resolveIngestionRunReachedStage(run) {
  const rawStage = readNormalizedStage(run?.lastCompletedStage);
  const aliasedStage = INGESTION_RUN_STAGE_ALIASES[rawStage] ?? rawStage;
  if (INGESTION_RUN_STAGES.includes(aliasedStage)) {
    return aliasedStage;
  }
  return inferReachedStage(run);
}

function isReachedStageCurrent(run, reachedStage) {
  if (!reachedStage) {
    return false;
  }
  if (run?.status === 'failure') {
    return true;
  }
  if (reachedStage === 'applying' && run?.applyStatus !== 'applied') {
    return true;
  }
  if (!IN_PROGRESS_STAGES.has(reachedStage)) {
    return false;
  }
  return run?.status === 'pending' || run?.status === 'running';
}

function buildStageItem(stage, state) {
  return {
    stage,
    label: formatIngestionRunEnumLabel(stage),
    state,
    ariaLabel: `${formatIngestionRunEnumLabel(stage)}, ${state}`,
  };
}

export function resolveIngestionRunStageStates(run) {
  const reachedStage = resolveIngestionRunReachedStage(run);
  const reachedIndex = INGESTION_RUN_STAGES.indexOf(reachedStage);
  const reachedIsCurrent = isReachedStageCurrent(run, reachedStage);
  const isFailed = run?.status === 'failure';
  const isInFlight = run?.status === 'pending' || run?.status === 'running';

  let currentIndex = -1;
  let completedThroughIndex = -1;

  if (reachedIndex < 0) {
    if (isInFlight) {
      currentIndex = 0;
    }
  } else if (reachedIsCurrent) {
    currentIndex = reachedIndex;
    completedThroughIndex = reachedIndex - 1;
  } else {
    completedThroughIndex = reachedIndex;
    if (reachedIndex < INGESTION_RUN_STAGES.length - 1) {
      currentIndex = reachedIndex + 1;
    }
  }

  return INGESTION_RUN_STAGES.map((stage, stageIndex) => {
    if (stageIndex === currentIndex) {
      const state = isFailed && stageIndex === reachedIndex
        ? INGESTION_STAGE_STATE_ERROR
        : INGESTION_STAGE_STATE_CURRENT;
      return buildStageItem(stage, state);
    }
    if (stageIndex <= completedThroughIndex) {
      return buildStageItem(stage, INGESTION_STAGE_STATE_COMPLETED);
    }
    return buildStageItem(stage, INGESTION_STAGE_STATE_UPCOMING);
  });
}

export function shouldShowIngestionRunStageStepper(run) {
  return isExtractionRun(run);
}
